import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { type GraphQLConnection } from "@/lib/graphql-pagination";
import { format } from "date-fns";

// --- Types ---

export interface WeeklyCollectionTrendPoint {
  week: string;
  weekStartDate: string;
  collected: number;
  transactionCount: number;
}

export interface WeeklyCollectionTrendResult {
  points: WeeklyCollectionTrendPoint[];
  totalCollected: number;
  averagePerWeek: number;
}

// --- GraphQL ---

interface WeeklyCollectionTrendNode {
  creditControllerId: number | null;
  weekStartDate: string;
  collectedAmount: number;
  transactionCount: number;
}

interface WeeklyCollectionTrendListResponse {
  dashboard: {
    weeklyCollectionTrendList: GraphQLConnection<WeeklyCollectionTrendNode>;
  };
}

const WEEKLY_COLLECTION_TREND_QUERY = `
  query WeeklyCollectionTrendList($first: Int, $filter: WeeklyCollectionTrendFilterInput) {
    dashboard {
      weeklyCollectionTrendList(first: $first, filter: $filter) {
        edges {
          node {
            creditControllerId
            weekStartDate
            collectedAmount
            transactionCount
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Mock data generator ---

function generateMockTrend(from: Date, to: Date): WeeklyCollectionTrendPoint[] {
  const points: WeeklyCollectionTrendPoint[] = [];
  const cursor = new Date(from);
  let seed = from.getTime();
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    return Math.abs(seed) / 0xffffffff;
  };
  while (cursor <= to) {
    points.push({
      week: format(cursor, "MMM d"),
      weekStartDate: cursor.toISOString().split("T")[0],
      collected: Math.round(100000 + rand() * 80000),
      transactionCount: Math.round(5 + rand() * 20),
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  return points;
}

// --- Hook ---

export function useWeeklyCollectionTrendGraphQL(
  selectedUserId?: string,
  dateFrom?: Date,
  dateTo?: Date,
  currencyCode?: string,
) {
  const apiConfig = useApiConfig();

  const effectiveFrom = dateFrom ?? new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const effectiveTo = dateTo ?? new Date();

  const mockPoints = generateMockTrend(effectiveFrom, effectiveTo);
  const mockTotal = mockPoints.reduce((s, p) => s + p.collected, 0);
  const mockResult: WeeklyCollectionTrendResult = {
    points: mockPoints,
    totalCollected: mockTotal,
    averagePerWeek: mockPoints.length > 0 ? Math.round(mockTotal / mockPoints.length) : 0,
  };

  return useModeAwareQuery<WeeklyCollectionTrendResult, Error>(
    ["weekly-collection-trend", selectedUserId, dateFrom?.toISOString(), dateTo?.toISOString(), currencyCode],
    async ({ signal }) => {
      const filter: Record<string, unknown> = {};

      if (selectedUserId && selectedUserId !== "everyone") {
        filter.creditControllerId = { eq: Number(selectedUserId) };
      }

      if (currencyCode) {
        filter.currencyCode = { eq: currencyCode };
      }

      const adjustedFrom = new Date(effectiveFrom);
      adjustedFrom.setDate(adjustedFrom.getDate() - 6);

      filter.weekStartDate = {
        ge: adjustedFrom.toISOString().split("T")[0],
        le: effectiveTo.toISOString().split("T")[0],
      };

      const response = await graphqlQuery<WeeklyCollectionTrendListResponse>(
        apiConfig,
        WEEKLY_COLLECTION_TREND_QUERY,
        { first: 200, filter },
        signal,
      );

      const nodes = response.dashboard.weeklyCollectionTrendList.edges.map(x => x.node);

      // Sort by weekStartDate ascending
      nodes.sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));

      const points: WeeklyCollectionTrendPoint[] = nodes.map(x => ({
        week: format(new Date(x.weekStartDate), "MMM d"),
        weekStartDate: x.weekStartDate,
        collected: x.collectedAmount,
        transactionCount: x.transactionCount,
      }));

      const totalCollected = points.reduce((s, p) => s + p.collected, 0);

      return {
        points,
        totalCollected,
        averagePerWeek: points.length > 0 ? Math.round(totalCollected / points.length) : 0,
      };
    },
    mockResult,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}

// --- Hook for all users (team overview) ---

export function useWeeklyCollectionTrendAllUsers(
  dateFrom?: Date,
  dateTo?: Date,
  currencyCode?: string,
) {
  const apiConfig = useApiConfig();

  const effectiveFrom = dateFrom ?? new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const effectiveTo = dateTo ?? new Date();

  return useModeAwareQuery<Map<number, number>, Error>(
    ["weekly-collection-trend-all-users", dateFrom?.toISOString(), dateTo?.toISOString(), currencyCode],
    async ({ signal }) => {
      const adjustedFrom = new Date(effectiveFrom);
      adjustedFrom.setDate(adjustedFrom.getDate() - 6);

      const filter: Record<string, unknown> = {
        weekStartDate: {
          ge: adjustedFrom.toISOString().split("T")[0],
          le: effectiveTo.toISOString().split("T")[0],
        },
      };

      if (currencyCode) {
        filter.currencyCode = { eq: currencyCode };
      }

      const response = await graphqlQuery<WeeklyCollectionTrendListResponse>(
        apiConfig,
        WEEKLY_COLLECTION_TREND_QUERY,
        { first: 1000, filter },
        signal,
      );

      const nodes = response.dashboard.weeklyCollectionTrendList.edges.map(x => x.node);

      // Group by creditControllerId and sum collectedAmount
      const byUser = new Map<number, number>();
      for (const node of nodes) {
        if (node.creditControllerId !== null) {
          const current = byUser.get(node.creditControllerId) ?? 0;
          byUser.set(node.creditControllerId, current + node.collectedAmount);
        }
      }

      return byUser;
    },
    new Map<number, number>(),
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}
